/**
 * Credit Union Product Compare
 * @author Oliver Boermans
 */

//
// Utilities
import debug from "debug";
const MODULE_NAME = 'cu-product-compare';
const log = debug(MODULE_NAME);

//
// Dependencies
import uniqueId from 'lodash/uniqueId';
import ease from '@vendor/eases/sine-in-out';
import debounce from 'lodash/debounce';
import scrollmonitor from '@vendor/scrollmonitor';

//
// Constants
const pc = {
	isCollapsed: false,
	hasFiltered: false,
	hasHidden: false
};

const TABLE_SELECTOR = 'table[data-cu-pc-table]';
const TABLE_COLLAPSED_CLASS = 'is-collapsed';

const SCROLL_PANE_SELECTOR = 'div[data-cu-pc-pane]';
const SCROLL_BUTTON_LEFT_SELECTOR = 'button[data-cu-pc-scroll-left]';
const SCROLL_BUTTON_RIGHT_SELECTOR = 'button[data-cu-pc-scroll-right]';
const SCROLL_EDGE_THRESHOLD = 5;
const SCROLL_DURATION = 500;

const STICKY_ELEMENT_SELECTOR = 'div[data-cu-pc-sticky]';
const STICKY_WATCH_SELECTOR = 'div[data-cu-pc-sticky-watch]';
const STICKY_WATCH_OFFSETS = {top: 0, bottom: -350};
const STICKY_CLASS = 'is-stuck';

const TOGGLE_MORE_BUTTON_SELECTOR = 'button[data-cu-pc-toggle-more]';
const TOGGLE_MORE_BUTTON_LABEL_SELECTOR = 'span[data-cu-pc-toggle-more-label]';
const TOGGLE_MORE_BUTTON_COLLAPSED_CLASS = 'is-collapsed';
const TOGGLE_MORE_BUTTON_OPEN_LABEL = 'Show less';
const TOGGLE_MORE_BUTTON_COLLAPSED_LABEL = 'Show more';

const RESET_BUTTON_SELECTOR = 'button[data-pc-product-reset]';

const HIDE_PRODUCT_BUTTON_SELECTOR = 'button[data-pc-hide-product]';
const HIDE_PRODUCT_CLASS_PREFIX = 'has-hidden-product-';

const PRODUCT_HEAD_CELL_SELECTOR = 'th[data-cu-pc-head-cell]';
const PRODUCT_HEAD_LINK_SELECTOR = 'a[data-cu-pc-head-cell-link]';

const FILTER_CATEGORY_CLASS_PREFIX = 'has-filter-product-category-';
const CATEGORY_FILTER_INPUT_SELECTOR = 'input[data-pc-category-filter]';

const COLUMN_RULER_CLASS = 'cu-pc-column-ruler';

const enableButton = function enableButton (button, enable) {
	button.attr('disabled', enable ? null : 'disabled');
};

const focusNextProduct = function focusNextProduct (fromCell) {
	let toFocus = fromCell
		.nextAll()
		.filter(':visible')
		.first();

	//	Focus first following visible product
	if (toFocus.length) {
		toFocus.find(PRODUCT_HEAD_LINK_SELECTOR).focus();
	} else {
		//	Look for previous instead
		toFocus = fromCell
			.prevAll(PRODUCT_HEAD_CELL_SELECTOR)
			.filter(':visible')
			.first();

		//	if visible focus it
		if (toFocus.length) {
			toFocus.find(PRODUCT_HEAD_LINK_SELECTOR).focus();
		} else {
			//	There are no remaining visible products
			//	Focus reset button
			this.resetButton.focus();
		}
	}
};

const hideProduct = function hideProduct (id, button) {
	const headCell = button.closest(PRODUCT_HEAD_CELL_SELECTOR);
	this.compareTable.addClass(`${HIDE_PRODUCT_CLASS_PREFIX}${id}`);

	//	Enable reset button
	enableButton(this.resetButton, true);
	focusNextProduct.call(this, headCell);
	this.hasHidden = true;
	updateScrollButtonState.apply(this);
};

const updateScrollButtonState = function updateScrollButtonState () {
	const pane = this.scrollPane[0];

	const scrollLeft = pane.scrollLeft;
	const scrollLeftMax = pane.scrollWidth - pane.clientWidth;

	const canScrollLeft = scrollLeft > SCROLL_EDGE_THRESHOLD;
	const canScrollRight = scrollLeftMax - scrollLeft > SCROLL_EDGE_THRESHOLD;

	enableButton(this.scrollButtonLeft, canScrollLeft);
	enableButton(this.scrollButtonRight, canScrollRight);
};

const smoothScrollPane = function smoothScrollPane (param, timestamp) {
	//	param = {startPos, direction, duration [, startTime]}

	const startTime = param.startTime !== undefined ? param.startTime : timestamp;
	const startPos = param.startPos;
	const direction = param.direction;
	const duration = param.duration;
	const pane = this.scrollPane[0];
	const scrollStep = this.columnRuler.width();

	const timeElapsed = timestamp - startTime;
	const easeElapsed = timeElapsed/duration;
	const targetScrollDist = scrollStep * direction;

	pane.scrollLeft = startPos + (targetScrollDist * ease(easeElapsed) );

	if (timeElapsed < duration) {
		window.requestAnimationFrame(smoothScrollPane.bind(this, {
				startPos: startPos,
				direction: direction,
				duration: duration,
				startTime: startTime
			}));
	}
};

const doScrollLeft = function doScrollLeft () {
	const pane = this.scrollPane[0];

	//	TODO test for requestAnimationFrame support
	//	If none jump straight to target
	window.requestAnimationFrame(smoothScrollPane.bind(this, {
			startPos: pane.scrollLeft,
			direction: -1,
			duration: SCROLL_DURATION
		}));
};

const doScrollRight = function doScrollRight () {
	const pane = this.scrollPane[0];

	window.requestAnimationFrame(smoothScrollPane.bind(this, {
			startPos: pane.scrollLeft,
			direction: 1,
			duration: SCROLL_DURATION
		}));
};

const removeClassesPrefixed = function removeClassesPrefixed ($element, prefix) {
	const classPrefixLength = prefix.length;
	const classesToRemove = $element.attr('class')
		.split(' ')
		.filter(function(aClass){
			return aClass.slice(0, classPrefixLength) === prefix;
		});
 	$element.removeClass(classesToRemove.join(' '));
};

const unHideProducts = function unHideProducts () {

	//	Re-display individually hidden products
	removeClassesPrefixed(this.compareTable, HIDE_PRODUCT_CLASS_PREFIX);

 	//	Reset category filter
	removeClassesPrefixed(this.compareTable, FILTER_CATEGORY_CLASS_PREFIX);
	if (this.categoryFilterInputs) {

		this.categoryFilterInputs.val(['0']);

	}

	//	Reset reset
	enableButton(this.resetButton, false);
	this.hasFiltered = false;
	this.hasHidden = false;

	updateScrollButtonState.apply(this);
};

const showLess = function showLess () {
	this.compareTable.addClass(TABLE_COLLAPSED_CLASS);
	this.toggleMoreButtons
		.addClass(TOGGLE_MORE_BUTTON_COLLAPSED_CLASS)
		.find(TOGGLE_MORE_BUTTON_LABEL_SELECTOR)
			.text(TOGGLE_MORE_BUTTON_COLLAPSED_LABEL);
	this.isCollapsed = true;
};

const showMore = function showMore () {
	this.compareTable.removeClass(TABLE_COLLAPSED_CLASS);
	this.toggleMoreButtons
		.removeClass(TOGGLE_MORE_BUTTON_COLLAPSED_CLASS)
		.find(TOGGLE_MORE_BUTTON_LABEL_SELECTOR)
			.text(TOGGLE_MORE_BUTTON_OPEN_LABEL);
	this.isCollapsed = false;
};

const toggleMore = function handleToggleButtonClick () {
	let toggle = this.isCollapsed ? showMore : showLess;
	toggle.apply(this);

	//	Height of the table has changed
	//	Let the sticky buttons know
	scrollmonitor.update();
};

const applyFilter = function applyFilter (category) {

	//	Remove existing category filter classes
	removeClassesPrefixed(this.compareTable, FILTER_CATEGORY_CLASS_PREFIX);

	//	add new filter class
	this.compareTable.addClass(`${FILTER_CATEGORY_CLASS_PREFIX}${category}`);

	//	update reset state
	if (category != 0) {

		enableButton(this.resetButton, true);
		this.hasFiltered = true;

	//	check that no items are hidden before resetting
	} else if (!this.hasHidden) {

		enableButton(this.resetButton, false);
		this.hasFiltered = false;
	}

	updateScrollButtonState.apply(this);
};

const onFilterChange = function onFilterChange () {
	const category = $('input[name="result_filter"]:checked').val();
	applyFilter.call(this, category);
};

//	Triggered when short table all visible in viewport
//	and when tall fills the viewport
const onStickyFit = function stickButtons (element) {
	log("stickButtons");

	//	Do not stick buttons if table is contained in viewport
	if (this.isAboveViewport && this.isBelowViewport) {
		$(element).addClass(STICKY_CLASS);
	} else {
		$(element).removeClass(STICKY_CLASS);
	}
};

const unStickButtons = function unStickButtons (element) {
	log("unStickButtons");
	$(element).removeClass(STICKY_CLASS);
};

const stickyWatchEnters = function stickyWatchEnters (element) {
	log("stickyWatchEnters");

	//	Stick buttons when scrolling up from below
	if (this.isAboveViewport) {
		$(element).addClass(STICKY_CLASS);
	}
};

const stickyWatchExitsBottom = function stickyWatchExitsBottom (element) {
	log("stickyWatchExitsBottom");

	if (this.isAboveViewport) {
		$(element).removeClass(STICKY_CLASS);
	}
};

const stickyWatchExitsTop = function stickyWatchExitsTop (element) {
	log("stickyWatchExitsTop");

	if (this.isBelowViewport) {
		$(element).removeClass(STICKY_CLASS);
	}
};


/**
 * Initialises the product selector.
 * @param {Element} element
 */
export default function init (element) {
	log('init', element);
	element.id = uniqueId('cu-pc');

	//	Find elements
	pc.compareTable = $(TABLE_SELECTOR, element);

	if (!pc.compareTable.length) {
		log('No comparison table, bye…');
		return false;
	}

	pc.scrollPane = $(SCROLL_PANE_SELECTOR, element);
	pc.scrollButtonLeft = $(SCROLL_BUTTON_LEFT_SELECTOR, element);
	pc.scrollButtonRight = $(SCROLL_BUTTON_RIGHT_SELECTOR, element);
	pc.toggleMoreButtons = $(TOGGLE_MORE_BUTTON_SELECTOR, element);
	pc.hideProductButtons = $(HIDE_PRODUCT_BUTTON_SELECTOR, element);
	pc.resetButton = $(RESET_BUTTON_SELECTOR, element);
	pc.categoryFilterInputs = $(CATEGORY_FILTER_INPUT_SELECTOR, element);
	pc.stickyButtons = $(STICKY_ELEMENT_SELECTOR, element);
	pc.stickyWatchSubject = $(STICKY_WATCH_SELECTOR, element);


	//	Insert column ruler
	//	Width determined by CSS
	//	Measured to determine scroll distances
	pc.columnRuler = $('<div></div>', {
		'class': COLUMN_RULER_CLASS
	}).prependTo(element);

	const watcher = scrollmonitor.create(pc.stickyWatchSubject, STICKY_WATCH_OFFSETS);

	//	Event Handlers
	pc.toggleMoreButtons.on('click', toggleMore.bind(pc));
	pc.hideProductButtons.each(function(index,button){
		const $button = $(button);
		const productId = $button.data('pc-hide-product');

		$button.on('click', hideProduct.bind(pc, productId, $button));
	});
	pc.resetButton.on('click', unHideProducts.bind(pc));
	pc.scrollPane.on('scroll', debounce(updateScrollButtonState.bind(pc), 250));
	$(window).on('resize', debounce(updateScrollButtonState.bind(pc), 250));
	pc.scrollButtonLeft.on('click', doScrollLeft.bind(pc));
	pc.scrollButtonRight.on('click', doScrollRight.bind(pc));

	if (pc.categoryFilterInputs.length) {
		pc.categoryFilterInputs.on('change', onFilterChange.bind(pc));
	}

	//	stick buttons when table top meets top of viewport
	watcher.fullyEnterViewport(onStickyFit.bind(watcher, pc.stickyButtons));

	//	stick buttons when table bottom enters from top of viewport (with offset)
	watcher.enterViewport(stickyWatchEnters.bind(watcher, pc.stickyButtons));

	//	unstick buttons when table top falls below top of viewport
	watcher.partiallyExitViewport(stickyWatchExitsTop.bind(watcher, pc.stickyButtons));

	//	unstick buttons when table bottom exists top of viewport (with offset)
	watcher.exitViewport(stickyWatchExitsBottom.bind(watcher, pc.stickyButtons));

	//	initial state
	enableButton(pc.resetButton, false);
	showLess.apply(pc);
	onFilterChange.apply(pc);
	updateScrollButtonState.apply(pc);

	return;
}




// WEBPACK FOOTER //
// ./project/client/src/core/modules/cu-product-compare/index.js